/**
 * Access requests — query + mutation hooks.
 *
 * Approve and dismiss go through withStepUp: both live in the
 * INVITE permission group on the backend, which is step-up gated,
 * and approving issues an enrollment invitation.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import {
  accessRequestListSchema,
  approveResponseSchema,
  type AccessRequestList,
  type ApproveResponse,
} from "./schemas";

export const accessRequestsQueryKey = (tenantID: string) =>
  ["access-requests", tenantID] as const;

const base = (tenantID: string) =>
  `/api/v1/tenants/${encodeURIComponent(tenantID)}/access-requests`;

export async function fetchAccessRequests(tenantID: string): Promise<AccessRequestList> {
  const raw = await apiFetch<unknown>(base(tenantID));
  return accessRequestListSchema.parse(raw);
}

export function useAccessRequestsQuery(tenantID: string) {
  return useQuery<AccessRequestList>({
    queryKey: accessRequestsQueryKey(tenantID),
    queryFn: () => fetchAccessRequests(tenantID),
    // 30s, matching the recovery-request inbox: somebody locked out
    // of their console is waiting on this, and an operator who
    // refreshes to see whether a colleague already handled it is the
    // signal the interval is too long.
    refetchInterval: 30_000,
  });
}

export function useApproveAccessRequestMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<ApproveResponse, Error, { id: string; note?: string }>({
    mutationFn: async ({ id, note }) => {
      const raw = await withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(id)}/approve`, {
          method: "POST",
          body: { note: note ?? "" },
        }),
      );
      return approveResponseSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accessRequestsQueryKey(tenantID) });
    },
  });
}

export function useDismissAccessRequestMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string; note?: string }>({
    mutationFn: async ({ id, note }) => {
      await withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(id)}/dismiss`, {
          method: "POST",
          body: { note: note ?? "" },
        }),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accessRequestsQueryKey(tenantID) });
    },
  });
}
